import { useSearchParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
//@ts-ignore
import { mask } from 'remask';
//
import { decodeBase64 } from '../../functions/crypt';
//
import apiPlay from '../../services/apiPlay';
//
import {
  Typography,
  Container,
  TextField,
  Skeleton,
  Divider,
  Paper,
  Box,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';

/**
 * Layout no padrão do new-sgm-v2: card centralizado na viewport, borda de 1px
 * com a sombra sutil do tema (--card-shadow) e o degradê radial nos cantos que
 * o Card do SGM aplica. A lógica das três etapas (verificacpf -> portincpf ->
 * portintoken) é a mesma de antes; só a apresentação mudou.
 */

/**
 * O backend responde com `descricao` explicando o motivo (CPF que nao confere,
 * codigo expirado, codigo incorreto...). Mostrar sempre a mesma frase generica
 * fazia o cliente repetir a mesma tentativa sem saber o que estava errado.
 */
function mensagemDoErro(error: any, padrao: string): string {
  const dados = error?.response?.data;
  return dados?.descricao || dados?.erro || padrao;
}

const cardSx = {
  width: '100%',
  p: 3,
  borderRadius: 'var(--radius-card)',
  border: '1px solid var(--border)',
  backgroundColor: 'var(--card)',
  boxShadow: 'var(--card-shadow)',
  backgroundImage: [
    'radial-gradient(circle at top right, color-mix(in oklab, var(--primary) 5%, transparent) 0%, transparent 35%)',
    'radial-gradient(circle at bottom left, color-mix(in oklab, var(--primary) 5%, transparent) 0%, transparent 35%)',
  ].join(', '),
};

/** Centraliza o conteúdo verticalmente em vez do mt: 25 fixo, que empurrava o
 *  card para fora da tela em telas baixas e no celular. */
const telaSx = {
  /* 100dvh acompanha a barra de endereço do navegador no celular. O fallback
     vai por @supports e NÃO por array: no sx do MUI, array significa
     breakpoints — `['100vh','100dvh']` viraria xs:100vh / sm:100dvh. */
  minHeight: '100vh',
  '@supports (min-height: 100dvh)': { minHeight: '100dvh' },
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  py: 4,
};

export default function Portabilidade() {

  const [searchParams] = useSearchParams();

  const [detalhesLinha, setDetalhesLinha] = useState<{ [key: string]: any } | null>(null);
  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingBtn, setLoadingBtn] = useState(false);
  const [isSolicita, setIsSolicita] = useState(true);
  const [concluido, setConcluido] = useState(false);
  const [isPf, setIsPf] = useState(true);
  const [cod, setCod] = useState('');
  const [cpf, setCpf] = useState('');

  const msisdn = searchParams.get('m') ? decodeBase64(searchParams.get('m') || '') : '';

  function verificaCpf() {

    setLoadingBtn(true);

    let dados = {
      msisdn: msisdn,
      pmsisdn: detalhesLinha?.tempmsisdn,
      cpf: cpf,
    }

    apiPlay.post('/api/portabilidade/portincpf', dados)
      .then(response => {
        toast.success('Dados confirmados.');
        setLoadingBtn(false);
        setIsSolicita(false);

      }).catch(error => {
        console.log('portincpf', error);
        toast.error(mensagemDoErro(error, 'Erro ao confirmar dados'));
        setLoadingBtn(false);
      })
  }

  function confirmaPortabilidade() {

    setLoadingBtn(true);

    let dados = {
      pmsisdn: detalhesLinha?.tempmsisdn,
      token: cod,
    }

    apiPlay.post('/api/portabilidade/portintoken', dados)
      .then(response => {
        setCod('');
        setConcluido(true);
        setLoadingBtn(false);

      }).catch(error => {
        console.log('portintoken', error);
        toast.error(mensagemDoErro(error, 'Erro ao confirmar portabilidade'));
        setLoadingBtn(false);
      })
  }

  useEffect(() => {
    if (!msisdn) return;

    setLoadingPage(true);

    let dadosApiVer = {
      msisdn: msisdn,
    }

    apiPlay.post('/api/portabilidade/verificacpf', dadosApiVer)
      .then((response) => {
        setDetalhesLinha(response.data);
        setIsPf(response.data?.cpf.length === 11);
        response.data.portability === 1 && setConcluido(true);
        setLoadingPage(false);

      }).catch((error) => {
        console.log(error);//LOG
        toast.error(mensagemDoErro(error, 'Erro ao buscar detalhes da solicitação'));

      })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!msisdn) {
    return (
      <Box sx={telaSx}>
        <Container component="section" maxWidth="xs">
          <Paper sx={cardSx}>
            <Typography variant="h5" textAlign="center" gutterBottom>
              Link inválido
            </Typography>
            <Typography variant="subtitle1" textAlign="center" color="text.secondary">
              Não foi possível identificar a solicitação. Verifique o link recebido
              ou entre em contato com o seu atendimento.
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  if (loadingPage) {
    return (
      <Box sx={telaSx}>
        <Container component="section" maxWidth="xs">
          <Skeleton variant="rounded" width={'100%'} height={340} />
        </Container>
      </Box>
    )
  }

  if (concluido) {
    return (
      <Box sx={telaSx}>
        <Container component="section" maxWidth="xs">
          <Paper sx={cardSx}>
            <Typography variant="h4" textAlign="center" sx={{ mb: 1.5 }}>
              Tudo certo!
            </Typography>
            <Typography variant="subtitle1" textAlign="center" color="text.secondary">
              Seu pedido de portabilidade está confirmado. Você será avisado quando
              a transferência do número for concluída.
            </Typography>
          </Paper>
        </Container>
      </Box>
    )
  }

  return (
    <Box sx={telaSx}>
      <Container component="section" maxWidth="xs">
        <Paper sx={cardSx}>
          <Typography variant="h5" textAlign="center">
            {isSolicita
              ? 'Solicitação de Portabilidade'
              : 'Confirmação de Portabilidade'
            }
          </Typography>

          <Divider sx={{ my: 2.5 }} />

          <Typography
            variant="subtitle1"
            textAlign="center"
            color="text.secondary"
          >
            {isSolicita
              ? 'Confirme os dados abaixo para prosseguir com a portabilidade:'
              : 'Digite o código enviado para o número abaixo:'
            }
          </Typography>

          <Typography
            textAlign="center"
            sx={{ mt: 1, fontWeight: 600, letterSpacing: '0.01em' }}
          >
            {isSolicita
              ? (isPf
                ? `XXX.XXX.${detalhesLinha?.cpf.slice(6, 9)}-XX`
                : `XX.XXX.${detalhesLinha?.cpf.slice(5, 8)}/XXXX-XX`)
              : (detalhesLinha?.tempmsisdn
                ? mask(detalhesLinha.tempmsisdn.slice(2), ['(99) 99999-9999'])
                : '-')
            }
          </Typography>

          <Box
            component="form"
            onSubmit={e => { e.preventDefault(); isSolicita ? verificaCpf() : confirmaPortabilidade() }}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              mt: 2.5,
            }}
          >
            {isSolicita
              ?
              <TextField
                type='tel'
                id='id_cpf'
                label={isPf ? 'CPF' : 'CNPJ'}
                placeholder={isPf ? '000.000.000-00' : '00.000.000/0000-00'}
                value={isPf ? mask(cpf, ['999.999.999-99']) : mask(cpf, ['99.999.999/9999-99'])}
                onChange={e => setCpf(isPf ? mask(e.target.value, ['99999999999']) : mask(e.target.value, ['99999999999999']))}
                variant='filled'
                fullWidth
                required
              />
              :
              <TextField
                type='tel'
                id='id_codigo'
                label='Código de confirmação'
                placeholder='000000'
                value={cod}
                onChange={e => setCod(e.target.value.replace(/\D/g, ''))}
                variant='filled'
                fullWidth
                autoFocus
                required
                inputProps={{ minLength: 6, maxLength: 6, inputMode: 'numeric' }}
              />
            }

            <LoadingButton
              type="submit"
              fullWidth
              variant="contained"
              size='large'
              sx={{ mt: 3 }}
              loading={loadingBtn}
            >
              {/* Antes o rótulo vinha de isPf: numa linha PJ o botão dizia
                  "Confirmar" já na etapa do CNPJ. Quem manda é a etapa. */}
              {isSolicita ? 'Prosseguir' : 'Confirmar'}
            </LoadingButton>
          </Box>

        </Paper>
      </Container>
    </Box>
  )
}
